import React, { useEffect, useState, useRef } from 'react';
import { Spinner } from 'reactstrap';
import './visual-vote-activity.css';
import { ErrorDisplayer } from '../../errors/error-displayer';
import { useTranslate } from "react-translate";
import { getUserColor, hsla } from '../../../utils/user-color-helper';

export function VisualVoteActivity(props) {

    const t = useTranslate("Common");
    const tv = useTranslate("VisualVote");
    const userColor = getUserColor(props.colorSeed);
    const bgColor = props.userColor ?? hsla(userColor, 1);
    const callHubAsync = props.callHubAsync;

    const STOP = "stopVote";
    const START = "startVote"

    const SPOT = "vote-spot";
    const SPOT_APPEAR = "vote-spot appear";
    const SPOT_DISAPPEAR = "vote-spot disappear";

    const MIN_DELTA = 5;
    const WIDTH_BREAKPOINT = 760;

    const [imageUrl, setImageUrl] = useState(null);
    const [spotActive, setSpotActive] = useState(false);
    const [spotClass, setSpotClass] = useState(SPOT);
    const [visualVoteState, setVisualVoteState] = useState(props.filesArgs ? START : STOP);
    const [error, setError] = useState(null);
    const spotRef = useRef(null);
    const containerRef = useRef(null);
    const isClicked = useRef(false);
    const coords = useRef({
        startX: 0,
        startY: 0,
        lastX: 0,
        lastY: 0,
        hasMoved: false,
        percentX: null,
        percentY: null
    });

    const prevCoords = useRef({ x: null, y: null });
    const isForceRotate = useRef(false);
    const imageRef = useRef(null);
    const visualDocContainerRef = useRef(null);
    const COORDINATES_KEY = "coordinates"
    const voteDaTaList = useRef([]);
    const documentId = useRef(null);

    

    function saveCoordinatesInLocalStorage(relativePosition) {

        const localStorageData = JSON.parse(localStorage.getItem(COORDINATES_KEY));

        if (localStorageData && localStorageData.sessionId !== props.sessionId) {
            localStorage.removeItem(COORDINATES_KEY);
            voteDaTaList.current = []
        }

        if (!relativePosition.relativePosition) {
            const index = voteDaTaList.current.findIndex((item) => item.documentId === documentId.current);
            if (index === -1) return;

            voteDaTaList.current.splice(index, 1);
            if (voteDaTaList.current.length === 0) {
                localStorage.removeItem(COORDINATES_KEY);
                return;
            }

            const coordinatesData = {
                sessionId: props.sessionId,
                voteData: voteDaTaList.current
            }
            localStorage.setItem(COORDINATES_KEY, JSON.stringify(coordinatesData));

        } else {
            const voteData = {
                documentId: documentId.current,
                voteCoordinates: {
                    x: relativePosition.relativePosition.x,
                    y: relativePosition.relativePosition.y
                }
            }

            const index = voteDaTaList.current.findIndex((item) => item.documentId === voteData.documentId);

            if (index === -1) {
                voteDaTaList.current.push(voteData);              
            } else {
                voteDaTaList.current[index] = voteData;
            }

            const coordinatesData = {
                sessionId: props.sessionId,
                voteData: voteDaTaList.current
            }
            localStorage.setItem(COORDINATES_KEY, JSON.stringify(coordinatesData));
        }
    }

    function getCoordinatesFromLocalStorage() {

        const localStorageData = JSON.parse(localStorage.getItem(COORDINATES_KEY));

        if (!localStorageData) return;
        if (localStorageData.sessionId !== props.sessionId) return;

        const coordinatesData = localStorageData.voteData.find((data) => data.documentId === documentId.current);

        if (!coordinatesData) return;
     
        const spot = spotRef.current;
        const { x, y } = coordinatesData.voteCoordinates;

        const container = containerRef.current;
        const scale = Math.max(container.clientWidth, container.clientHeight);
       
        const xInPx = (x / 100) * scale - spot.offsetWidth / 2;
        const yInPx = (y / 100) * scale - spot.offsetWidth / 2;
      
        spot.style.left = `${xInPx}px`;
        spot.style.top = `${yInPx}px`;

        coords.current.lastX = xInPx;
        coords.current.lastY = yInPx;

        coords.current.percentX = x;
        coords.current.percentY = y;

        setSpotActive(true);


    }

    function roundTo(num, precision) {
        const factor = Math.pow(10, precision)
        return Math.round(num * factor) / factor
    }

    function updateSpotPositionFromPercent() {
        if (!spotRef.current || !containerRef.current) return;

        const spot = spotRef.current;
        const container = containerRef.current;

        const scale = Math.max(container.clientWidth, container.clientHeight);

        const { percentX, percentY } = coords.current;

        if (percentX === null || percentY === null) return;

        const spotX = (percentX / 100) * scale - spot.offsetWidth / 2;
        const spotY = (percentY / 100) * scale - spot.offsetHeight / 2;

            spot.style.left = `${spotX}px`;
            spot.style.top = `${spotY}px`;

            coords.current.lastX = spotX;
            coords.current.lastY = spotY;
    };

    function handleAnimation() {
        if (spotClass === SPOT_APPEAR) {
            setSpotClass(SPOT);
        } else if (spotClass === SPOT_DISAPPEAR) {
            setSpotActive(false);
            setSpotClass(SPOT);
        }
    }

    useEffect(() => {
        const activityTitle = document.querySelector(".title-container")
        if (visualVoteState === START) {
            activityTitle.style.display = "none";
        } else {
            activityTitle.style.display = "block";
        }
    }, [visualVoteState])

    useEffect(() => {
        let controller = new AbortController();
        let signal = controller.signal;

        async function fetchFile(fileUri) {
            try {
                if (!fileUri) {
                    setError(t("error"));
                    return;
                }
                const response = await fetch(fileUri, { method: 'GET', signal });
                if (!response || !response.ok) {
                    setError(t("error"));
                    return;
                }
                const blob = await response.blob();
                if (!blob) {
                    setError(t("error"));
                    return;
                }
                const url = URL.createObjectURL(blob);
                if (!url) {
                    setError(t("error"));
                    return;
                }
                setImageUrl(url);
            } catch (e) {
                if (e.name !== 'AbortError') {
                    console.error(e);
                    setError(t("error"));
                }
            }
        }

        async function onCustomVisualVoteActivityMessage(eventName, jsonArgs) {
            setSpotActive(false);
            if (eventName === STOP) {
                setVisualVoteState(STOP);
            }
            else if (eventName === START) {
                const args = JSON.parse(jsonArgs);

                const fileUri = args.uri;
                documentId.current = args.id;
                await fetchFile(fileUri);
                setVisualVoteState(START);

            } else if (eventName === "deleteVotes") {

                const args = JSON.parse(jsonArgs);

                const coordsToDelete = voteDaTaList.current.findIndex((vote) => vote.documentId === args.id);
                if (coordsToDelete === undefined) return;
                voteDaTaList.current.splice(coordsToDelete, 1);
                const coordinatesData = {
                    sessionId: props.sessionId,
                    voteData: voteDaTaList.current
                }
                localStorage.setItem(COORDINATES_KEY, JSON.stringify(coordinatesData));
                if (voteDaTaList.current.length === 0) {
                    localStorage.removeItem(COORDINATES_KEY);
                }

            } else if (eventName === "deleteAllVotes") {
                const args = JSON.parse(jsonArgs);
                args.pageArgs.forEach((page) => {
                    voteDaTaList.current.forEach((data, index) => {
                        if (data.documentId !== page.id) return;
                        voteDaTaList.current.splice(index, 1);
                    })                   
                })

                const coordinatesData = {
                    sessionId: props.sessionId,
                    voteData: voteDaTaList.current
                }
                localStorage.setItem(COORDINATES_KEY, JSON.stringify(coordinatesData));
                if (voteDaTaList.current.length === 0) {
                    localStorage.removeItem(COORDINATES_KEY);
                }
                
            }
        };

        if (visualVoteState === START) {
            documentId.current = props.filesArgs.id;
            fetchFile(props.filesArgs.uri)
        }

        if (props.setCustomActivityMessage || props.filesArgs.uri) {
            props.setCustomActivityMessage(onCustomVisualVoteActivityMessage);
        }

        return () => {
            controller.abort();
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
            if (props.setCustomActivityMessage) {
                props.setCustomActivityMessage(null);
            }
        };
    }, [props.setCustomActivityMessage]);

    useEffect(() => {
        
        if (visualVoteState === START) {

            if (!spotRef.current || !containerRef.current || !imageUrl) return;

            const spot = spotRef.current;
            const container = containerRef.current;

            function getSpotCoordinates(e) {
                if (e.touches && e.touches.length > 0) {
                    if (isForceRotate.current) {
                        return {
                            x: - e.touches[0].clientY,
                            y: container.clientWidth - (- e.touches[0].clientX)
                        }
                    }
                    return {
                        x: e.touches[0].clientX,
                        y: e.touches[0].clientY
                    };
                }
                    return {
                    x: e.clientX,
                    y: e.clientY
                };
            };

            async function sendCoordinates(x, y) {
                const width = container.clientWidth;
                const height = container.clientHeight;

                let xPercent;
                let yPercent

                const scale = Math.max(height, width);

                let relativePosition;

                if (x !== null && y !== null) {
                    const xResult = ((x + spot.offsetWidth / 2) / scale) * 100;
                    const yResult = ((y + spot.offsetHeight / 2) / scale) * 100;
                    xPercent = roundTo(xResult, 2);
                    yPercent = roundTo(yResult, 2);
                    relativePosition = { relativePosition: { x: xPercent, y: yPercent } };

                } else {
                    xPercent = null;
                    yPercent = null;
                    relativePosition = { relativePosition: null };
                }

                if ((Math.round(xPercent) === Math.round(prevCoords.current.x)) &&
                    (Math.round(yPercent) === Math.round(prevCoords.current.y)))
                    return;
                if (xPercent === 0 || yPercent === 0)
                    return;

                await callHubAsync("addVisualVote", relativePosition);
                saveCoordinatesInLocalStorage(relativePosition)
                if (!xPercent && !yPercent)
                    return;
                prevCoords.current = { x: xPercent, y: yPercent };
                coords.current.percentX = xPercent;
                coords.current.percentY = yPercent;
            };

            function onMouseClick(e) {
                const rect = container.getBoundingClientRect();
                let clickX = e.clientX - rect.left;
                let clickY = e.clientY - rect.top;

                if (isForceRotate.current) {
                    const tempY = clickY;
                    clickY = clickX;
                    clickX = container.clientWidth - tempY;
                }
                

                const spotWidth = spot.offsetWidth;
                const spotHeight = spot.offsetHeight;

                const spotX = clickX - spotWidth / 2;
                const spotY = clickY - spotHeight / 2;

                const boundedX = Math.max(0, Math.min(spotX, container.clientWidth - spotWidth));
                const boundedY = Math.max(0, Math.min(spotY, container.clientHeight - spotHeight));

                spot.style.left = `${boundedX}px`;
                spot.style.top = `${boundedY}px`;

                coords.current.lastX = boundedX;
                coords.current.lastY = boundedY;

                sendCoordinates(boundedX, boundedY);

                setSpotActive(true);
                setSpotClass(SPOT);
                requestAnimationFrame(() => {
                    setSpotClass(SPOT_APPEAR);
                });
            };

            function onPointerDown(e) {
                e.preventDefault();
                isClicked.current = true;
                const { x, y } = getSpotCoordinates(e);
                coords.current.startX = x;
                coords.current.startY = y;
                coords.current.hasMoved = false;
            };

            function onPointerMove(e) {
                if (!isClicked.current) return;
                e.preventDefault();

                const { x, y } = getSpotCoordinates(e);
                const deltaX = x - coords.current.startX;
                const deltaY = y - coords.current.startY;

                if (Math.abs(deltaX) > MIN_DELTA || Math.abs(deltaY) > MIN_DELTA) {
                    coords.current.hasMoved = true;
                }

                const nextX = deltaX + coords.current.lastX;
                const nextY = deltaY + coords.current.lastY;

                const maxX = container.clientWidth - spot.offsetWidth;
                const maxY = container.clientHeight - spot.offsetHeight;

                const boundedX = Math.max(0, Math.min(nextX, maxX));
                const boundedY = Math.max(0, Math.min(nextY, maxY));

                spot.style.left = `${boundedX}px`;
                spot.style.top = `${boundedY}px`;
            };

            function onPointerUp() {
                if (!isClicked.current) return;
                isClicked.current = false;
                coords.current.lastX = spot.offsetLeft;
                coords.current.lastY = spot.offsetTop;

                sendCoordinates(coords.current.lastX, coords.current.lastY);
            };

            function onSpotClick(e) {
                e.stopPropagation();
                if (!coords.current.hasMoved) {
                    sendCoordinates(null, null)
                    setSpotClass(SPOT_DISAPPEAR);
                }
            };

            function onSpotTouchEnd(e) {
                e.stopPropagation();
                if (!coords.current.hasMoved) {
                    sendCoordinates(null, null)
                    setSpotClass(SPOT_DISAPPEAR);
                }
            };

            container.addEventListener('click', onMouseClick);
            spot.addEventListener('mousedown', onPointerDown);
            spot.addEventListener('mouseup', onPointerUp);
            container.addEventListener('mousemove', onPointerMove);
            container.addEventListener('mouseleave', onPointerUp);
            spot.addEventListener('click', onSpotClick);

            spot.addEventListener('touchstart', onPointerDown);
            spot.addEventListener('touchend', onPointerUp);
            container.addEventListener('touchmove', onPointerMove);
            spot.addEventListener('touchend', onSpotTouchEnd);


            return () => {
                container.removeEventListener('click', onMouseClick);
                spot.removeEventListener('mousedown', onPointerDown);
                spot.removeEventListener('mouseup', onPointerUp);
                container.removeEventListener('mousemove', onPointerMove);
                container.removeEventListener('mouseleave', onPointerUp);
                spot.removeEventListener('click', onSpotClick);

                spot.removeEventListener('touchstart', onPointerDown);
                spot.removeEventListener('touchend', onPointerUp);
                container.removeEventListener('touchmove', onPointerMove);
                spot.removeEventListener('touchend', onSpotTouchEnd);
            };

        }
    }, [imageUrl, spotActive, visualVoteState]);

    useEffect(() => {
        window.addEventListener('resize', updateSpotPositionFromPercent);
        return () => {
            window.removeEventListener('resize', updateSpotPositionFromPercent);
        };
    }, []);

    useEffect(() => {

        if (!imageUrl) return;

        handleImageContainerPosition()
        window.addEventListener('resize', handleLandscapeMode);
        getCoordinatesFromLocalStorage();
        
        return () => {
            window.removeEventListener('resize', handleLandscapeMode);
            document.body.classList.remove('rotate-container');
        };
    }, [imageUrl]);

    function handleLandscapeMode() {
        const imageContainer = imageRef.current;
        if (!imageContainer) return;

        const isMobile = window.screen.width < WIDTH_BREAKPOINT;
        const isPortrait = window.matchMedia("(orientation: portrait)").matches;
        const isImageLandscape = imageContainer.naturalWidth > imageContainer.naturalHeight;

        //Force rotate 90degres the body
        if (isMobile && isPortrait && isImageLandscape) {
            document.body.classList.add('rotate-container');
            imageRef.current.style.maxWidth = '100dvh';
            imageRef.current.style.maxHeight = '100vw';
            isForceRotate.current = true;

        } else {
            document.body.classList.remove('rotate-container');
            imageRef.current.style.maxWidth = '100vw';
            imageRef.current.style.maxHeight = '100dvh';
            isForceRotate.current = false;

        }

        updateSpotPositionFromPercent();
    }

    function handleImageContainerPosition() {

        const imageContainer = imageRef.current;
        if (!imageContainer) return;
        const rect = imageContainer.getBoundingClientRect();

        if (!rect.width || !rect.height) return;

        const isImageLandscape = rect.width > rect.height;

        if (!isImageLandscape) {            
            visualDocContainerRef.current.style.position = "absolute";
            visualDocContainerRef.current.style.top = "50%";
            visualDocContainerRef.current.style.left = "50%";
            visualDocContainerRef.current.style.transform = "translate(-50%, -50%)";
        }
    }

    useEffect(() => {
        const localstorageDataOnInit = JSON.parse(localStorage.getItem(COORDINATES_KEY));
        if (localstorageDataOnInit?.voteData) {
            voteDaTaList.current = [...localstorageDataOnInit.voteData];
        }
    }, []);

    if (error) {
        console.log(error)
        return <ErrorDisplayer message={error} />;
    } else if (visualVoteState === START) {
        return (
            <div className="visual-doc-container" ref={visualDocContainerRef }>
                {imageUrl ? (                   
                    <div ref={containerRef} className="image-wrapper">
                        <img src={imageUrl} ref={imageRef} alt="Vote visual" className="visual-image" onLoad={handleLandscapeMode} />
                        <div
                            ref={spotRef}
                            className={spotClass}
                            onAnimationEnd={handleAnimation}
                            style={{
                                backgroundColor: bgColor,
                                visibility: spotActive ? 'visible' : 'hidden'
                            }}
                        >
                        </div>
                            </div>
                ) : (
                    <Spinner color="light" className="spinner" />
                )}
            </div>
        );
    } else if (visualVoteState === STOP) {
        return (
            <p>{tv("stop")}</p>
        )
    }


}
